/**
 * Agrupa ítems de revisión del despacho (materialidad) por responsable asignado
 * (tablero fiscal Despacho + catálogo de staff: auxiliar, administrador, etc.).
 */

export const UNASSIGNED_AUX_ID = "__sin_auxiliar__";
export const UNASSIGNED_AUX_NAME = "Sin responsable asignado";
export const AUX_FILTER_ALL = "todos";

/**
 * @param {Array<{ user_id?: string, name?: string, role_on_client?: string }>} assignedUsers
 * @returns {{ id: string, name: string } | null}
 */
export function primaryAssignee(assignedUsers) {
  const list = assigneesForClient(assignedUsers);
  if (!list.length) return null;
  const raw = Array.isArray(assignedUsers) ? assignedUsers : [];
  const primary = raw.find((u) => String(u?.role_on_client || "").toLowerCase() === "primary");
  if (primary) {
    const id = String(primary?.user_id || "").trim();
    if (id) {
      return {
        id,
        name: String(primary?.name || "").trim() || "Responsable",
        despacho_role: String(primary?.despacho_role || "").trim(),
      };
    }
  }
  return list[0];
}

/**
 * Todos los responsables asignados a una empresa (auxiliar, admin, supervisor, etc.).
 * @param {Array<{ user_id?: string, name?: string, despacho_role?: string }>} assignedUsers
 * @returns {Array<{ id: string, name: string, despacho_role?: string }>}
 */
export function assigneesForClient(assignedUsers) {
  const list = Array.isArray(assignedUsers) ? assignedUsers : [];
  const byId = new Map();
  for (const u of list) {
    const id = String(u?.user_id || "").trim();
    if (!id || byId.has(id)) continue;
    byId.set(id, {
      id,
      name: String(u?.name || "").trim() || "Responsable",
      despacho_role: String(u?.despacho_role || "").trim(),
    });
  }
  return [...byId.values()];
}

/**
 * @param {Array<{ fiscal_client_id?: string, assigned_users?: array, razon_social?: string }>} boardRows
 * @returns {Map<string, Array<{ id: string, name: string, despacho_role?: string }>>}
 */
export function buildClientAuxiliaresMap(boardRows) {
  const map = new Map();
  for (const row of boardRows || []) {
    const cid = String(row?.fiscal_client_id || "").trim();
    if (!cid) continue;
    const auxList = assigneesForClient(row?.assigned_users);
    if (auxList.length) map.set(cid, auxList);
  }
  return map;
}

/** @deprecated Prefer buildClientAuxiliaresMap */
export function buildClientAuxiliarMap(boardRows) {
  const multi = buildClientAuxiliaresMap(boardRows);
  const map = new Map();
  for (const row of boardRows || []) {
    const cid = String(row?.fiscal_client_id || "").trim();
    if (!cid) continue;
    const primary = primaryAssignee(row?.assigned_users);
    if (primary) map.set(cid, primary);
    else {
      const list = multi.get(cid);
      if (list?.[0]) map.set(cid, list[0]);
    }
  }
  return map;
}

/**
 * @param {Map<string, Array<{ id: string, name: string }>>|Map<string, { id: string, name: string }>} clientAuxMap
 * @returns {Array<{ id: string, name: string, despacho_role?: string }>}
 */
export function listAuxiliaresFromMap(clientAuxMap) {
  const byId = new Map();
  for (const value of clientAuxMap.values()) {
    const list = Array.isArray(value) ? value : value ? [value] : [];
    for (const aux of list) {
      if (!aux?.id || byId.has(aux.id)) continue;
      byId.set(aux.id, aux);
    }
  }
  return [...byId.values()].sort((a, b) =>
    String(a.name).localeCompare(String(b.name), "es", { sensitivity: "base" }),
  );
}

/**
 * Staff activo del despacho para el selector (incluye administrador / supervisor / auxiliar).
 * @param {Array<{ id?: string, name?: string, email?: string, despacho_role?: string, status?: string }>} staffRows
 */
export function listAssignableStaff(staffRows) {
  return (staffRows || [])
    .filter((s) => s?.id && (s.status || "active") === "active")
    .map((s) => ({
      id: String(s.id).trim(),
      name: String(s.name || s.email || s.id).trim() || "Responsable",
      despacho_role: String(s.despacho_role || "").trim(),
    }))
    .sort((a, b) => String(a.name).localeCompare(String(b.name), "es", { sensitivity: "base" }));
}

export function staffRoleLabel(role) {
  const r = String(role || "").trim().toLowerCase();
  if (r === "auxiliar") return "Auxiliar";
  if (r === "consulta") return "Consulta";
  if (r === "supervisor") return "Supervisor";
  if (r === "admin" || r === "administrador" || r === "administrator") return "Administrador";
  if (r === "titular" || r === "owner") return "Titular";
  if (!r) return "";
  return r.charAt(0).toUpperCase() + r.slice(1);
}

function ensureAuxBucket(auxBuckets, auxId, auxName, despachoRole = "") {
  if (!auxBuckets.has(auxId)) {
    auxBuckets.set(auxId, {
      auxId,
      auxName,
      despacho_role: despachoRole || "",
      companies: new Map(),
    });
  } else if (despachoRole && !auxBuckets.get(auxId).despacho_role) {
    auxBuckets.get(auxId).despacho_role = despachoRole;
  }
  return auxBuckets.get(auxId);
}

function ensureCompany(bucket, clientId, clientName) {
  if (!bucket.companies.has(clientId)) {
    bucket.companies.set(clientId, {
      clientId,
      clientName,
      items: [],
    });
  }
  return bucket.companies.get(clientId);
}

/**
 * Agrupa faltantes por responsable.
 * - Si hay ``staffOptions``, siembra TODOS los usuarios activos del equipo
 *   (administrador, supervisor, auxiliar, …) aunque no tengan faltantes.
 * - Si hay ``boardRows``, siembra empresas asignadas a cada responsable.
 *
 * @param {Array<object>} items
 * @param {Map<string, Array<{ id: string, name: string }>>|Map<string, { id: string, name: string }>} clientAuxMap
 * @param {Record<string, string>} [clientNameById]
 * @param {Array<object>} [boardRows]
 * @param {Array<{ id: string, name: string, despacho_role?: string }>} [staffOptions]
 */
export function groupFaltantesByAuxiliar(
  items,
  clientAuxMap,
  clientNameById = {},
  boardRows = [],
  staffOptions = [],
) {
  const auxBuckets = new Map();

  const resolveAuxList = (clientId) => {
    const raw = clientAuxMap.get(clientId);
    if (!raw) return [];
    return Array.isArray(raw) ? raw : [raw];
  };

  // 1) Sembrar todo el staff activo (incluye administradores).
  for (const staff of staffOptions || []) {
    if (!staff?.id) continue;
    ensureAuxBucket(auxBuckets, staff.id, staff.name, staff.despacho_role || "");
  }

  // 2) Sembrar empresas del tablero bajo cada asignado.
  for (const row of boardRows || []) {
    const clientId = String(row?.fiscal_client_id || "").trim();
    if (!clientId) continue;
    const auxList = resolveAuxList(clientId);
    if (!auxList.length) continue;
    const clientName =
      String(row?.razon_social || "").trim()
      || clientNameById[clientId]
      || clientId;
    for (const aux of auxList) {
      const bucket = ensureAuxBucket(auxBuckets, aux.id, aux.name, aux.despacho_role || "");
      ensureCompany(bucket, clientId, clientName);
    }
  }

  // 3) Colocar faltantes.
  for (const item of items || []) {
    const clientId = String(item?.client_id || "").trim() || "__sin_empresa__";
    const auxList = resolveAuxList(clientId);
    const targets = auxList.length
      ? auxList
      : [{ id: UNASSIGNED_AUX_ID, name: UNASSIGNED_AUX_NAME }];
    const clientName =
      item?.client_name
      || clientNameById[clientId]
      || (clientId === "__sin_empresa__" ? "Sin empresa" : clientId);

    for (const aux of targets) {
      const bucket = ensureAuxBucket(
        auxBuckets,
        aux.id,
        aux.name,
        aux.despacho_role || "",
      );
      const company = ensureCompany(bucket, clientId, clientName);
      if (!company.clientName || company.clientName === clientId) {
        company.clientName = clientName;
      }
      company.items.push(item);
    }
  }

  const groups = [...auxBuckets.values()].map((bucket) => {
    const companies = [...bucket.companies.values()]
      .map((c) => ({ ...c, total: c.items.length }))
      .sort((a, b) =>
        String(a.clientName).localeCompare(String(b.clientName), "es", { sensitivity: "base" }),
      );
    return {
      auxId: bucket.auxId,
      auxName: bucket.auxName,
      despacho_role: bucket.despacho_role || "",
      companies,
      total: companies.reduce((n, c) => n + c.total, 0),
    };
  });

  groups.sort((a, b) => {
    if (a.auxId === UNASSIGNED_AUX_ID) return 1;
    if (b.auxId === UNASSIGNED_AUX_ID) return -1;
    if (a.total !== b.total) return b.total - a.total;
    return String(a.auxName).localeCompare(String(b.auxName), "es", { sensitivity: "base" });
  });

  return groups;
}

/**
 * @param {Array<{ auxId: string, auxName: string, companies?: array, total?: number }>} groups
 * @param {string} filterId — AUX_FILTER_ALL | auxId
 * @param {string} [searchQuery] — filtra por nombre/rol del responsable
 * @param {string} [empresaQuery] — filtra empresas por nombre, id o RFC
 * @param {Record<string, string>} [clientRfcById]
 */
export function filterAuxiliarGroups(
  groups,
  filterId = AUX_FILTER_ALL,
  searchQuery = "",
  empresaQuery = "",
  clientRfcById = {},
) {
  const q = String(searchQuery || "").trim().toLowerCase();
  const eq = String(empresaQuery || "").trim().toLowerCase();
  let out = Array.isArray(groups) ? groups : [];
  if (filterId && filterId !== AUX_FILTER_ALL) {
    out = out.filter((g) => g.auxId === filterId);
  }
  if (q) {
    out = out.filter((g) => {
      const name = String(g.auxName || "").toLowerCase();
      const role = String(g.despacho_role || "").toLowerCase();
      return name.includes(q) || role.includes(q);
    });
  }
  if (eq) {
    out = out
      .map((g) => {
        const companies = (g.companies || []).filter((c) => {
          const id = String(c.clientId || "").toLowerCase();
          // Coincidencia exacta por id (selector de empresa) o búsqueda parcial.
          if (id && id === eq) return true;
          const name = String(c.clientName || "").toLowerCase();
          const rfc = String(clientRfcById[c.clientId] || "").toLowerCase();
          return name.includes(eq) || id.includes(eq) || rfc.includes(eq);
        });
        const total = companies.reduce((n, c) => n + Number(c.total || c.items?.length || 0), 0);
        return { ...g, companies, total };
      })
      .filter((g) => (g.companies || []).length > 0);
  }
  return out;
}

/**
 * Ordena empresas: con faltantes → con CFDI en periodo → resto (A–Z).
 * @param {Array<{ clientId: string, clientName?: string, total?: number, items?: array }>} companies
 * @param {Record<string, number>} [cfdiCountsByClient]
 */
export function sortCompaniesByActivity(companies, cfdiCountsByClient = {}) {
  return [...(companies || [])].sort((a, b) => {
    const fa = Number(a.total || a.items?.length || 0);
    const fb = Number(b.total || b.items?.length || 0);
    if (fa !== fb) return fb - fa;
    const ca = Number(cfdiCountsByClient[a.clientId] || 0);
    const cb = Number(cfdiCountsByClient[b.clientId] || 0);
    if (ca !== cb) return cb - ca;
    return String(a.clientName || "").localeCompare(String(b.clientName || ""), "es", {
      sensitivity: "base",
    });
  });
}
